// covered-by: frontends/carrie/src/routes/ask-puntt/__tests__/chats.test.tsx
import { type AskPunttChatUserSummary } from "@mg/schemas/src/christo/askPuntt";
import {
  ChatCircle,
  Clock,
  MagnifyingGlass,
  Plus,
  Rows,
  SquaresFour,
  Users,
} from "@phosphor-icons/react";
import { Button, Callout, Flex, Text, TextField } from "@radix-ui/themes";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

import { askPunttRoute } from "./route";
import { projectMemberLabel } from "./utils/projectListMembers";
import { askPunttChatNavLink } from "./utils/chatNavigation";

import { AvatarWithInitials } from "../../components/AvatarWithInitials";
import { PageHeader } from "../../components/PageHeader";
import { useAppSelector } from "../../utils/hooks";
import { useAskPunttAllChats } from "../../utils/queries/askPuntt";

const VIEW_STORAGE_KEY = "askpuntt-chats-view";

type ChatsView = "grid" | "list";

type ChatListItem = {
  _id: string;
  title: string;
  userId?: string;
  ownerName?: string;
  role?: "owner" | "shared";
  projectId?: string | null;
  projectChatKind?: "shared" | "personal" | null;
  createdAt?: string;
  updatedAt?: string;
  members?: AskPunttChatUserSummary[];
};

function getStoredView(): ChatsView {
  try {
    const stored = localStorage.getItem(VIEW_STORAGE_KEY);
    if (stored === "list" || stored === "grid") return stored;
  } catch {
    // ignore
  }
  return "grid";
}

export function formatChatDate(iso: string | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function chatSubtitle(
  chat: ChatListItem,
  currentUserId: string | undefined,
): string | null {
  if (chat.userId !== currentUserId && chat.ownerName) {
    return `Owned by ${chat.ownerName}`;
  }
  if (chat.role === "shared") {
    return "Shared with you";
  }
  if (chat.projectId && chat.projectChatKind === "personal") {
    return "Project chat";
  }
  return null;
}

function ChatMembersFooter({
  members,
  chatId,
}: {
  members: AskPunttChatUserSummary[];
  chatId: string;
}) {
  const count = members.length;
  if (count === 0) {
    return (
      <span
        className="flex items-center gap-1 text-puntt-neutral-gray-9"
        data-testid={`chat-row-${chatId}-members`}
        aria-label="0 participants"
      >
        <Users size={14} aria-hidden="true" />
        <Text size="1">0</Text>
      </span>
    );
  }

  const visible = members.slice(0, 3);
  return (
    <span
      className="flex items-center gap-1"
      data-testid={`chat-row-${chatId}-members`}
      aria-label={`${count} participant${count === 1 ? "" : "s"}`}
    >
      <Flex gap="0" align="center" className="-space-x-1">
        {visible.map((m) => (
          <AvatarWithInitials
            key={m._id}
            size={6}
            name={projectMemberLabel(m)}
            avatar={m.avatar ?? undefined}
            className="ring-2 ring-base-white"
            aria-hidden="true"
          />
        ))}
      </Flex>
      {count > 3 ? (
        <Text size="1" className="text-puntt-neutral-gray-9">
          +{count - 3}
        </Text>
      ) : null}
    </span>
  );
}

function ChatCard({
  chat,
  currentUserId,
  onOpen,
}: {
  chat: ChatListItem;
  currentUserId: string | undefined;
  onOpen: (chat: ChatListItem) => void;
}) {
  const subtitle = chatSubtitle(chat, currentUserId);
  const updatedLabel = formatChatDate(chat.updatedAt ?? chat.createdAt);
  const members = chat.members ?? [];

  return (
    <button
      type="button"
      onClick={() => onOpen(chat)}
      data-testid={`chat-row-${chat._id}`}
      data-chat-id={chat._id}
      aria-label={`Open chat ${chat.title}`}
      className="group flex min-h-[140px] flex-col justify-between rounded-2xl border border-puntt-neutral-gray-6 bg-base-white p-5 text-left shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-puntt-accent-7 hover:shadow-md"
    >
      <div className="min-w-0">
        <Text
          as="div"
          size="4"
          weight="bold"
          className="truncate text-puntt-neutral-gray-12"
        >
          {chat.title}
        </Text>
        {subtitle ? (
          <Text
            size="2"
            className="mt-2 line-clamp-2 text-puntt-neutral-gray-10"
            data-testid={`chat-row-${chat._id}-subtitle`}
          >
            {subtitle}
          </Text>
        ) : null}
      </div>
      <Flex justify="end" align="center" gap="3" className="mt-4">
        {updatedLabel ? (
          <span
            className="flex items-center gap-1 text-puntt-neutral-gray-9"
            data-testid={`chat-row-${chat._id}-updated`}
          >
            <Clock size={14} aria-hidden="true" />
            <Text size="1">{updatedLabel}</Text>
          </span>
        ) : null}
        <ChatMembersFooter members={members} chatId={chat._id} />
      </Flex>
    </button>
  );
}

function ChatListRow({
  chat,
  currentUserId,
  onOpen,
}: {
  chat: ChatListItem;
  currentUserId: string | undefined;
  onOpen: (chat: ChatListItem) => void;
}) {
  const subtitle = chatSubtitle(chat, currentUserId);
  const members = chat.members ?? [];

  return (
    <button
      type="button"
      onClick={() => onOpen(chat)}
      data-testid={`chat-row-${chat._id}`}
      data-chat-id={chat._id}
      aria-label={`Open chat ${chat.title}`}
      className="flex w-full cursor-pointer items-center gap-4 rounded-lg border border-puntt-neutral-gray-5 bg-base-white p-4 text-left transition-all hover:border-puntt-accent-7 hover:shadow-sm"
    >
      <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-puntt-accent-2 text-puntt-accent-9">
        <ChatCircle size={20} weight="duotone" aria-hidden="true" />
      </div>
      <div className="min-w-0 flex-1">
        <Text
          size="3"
          weight="medium"
          className="block truncate text-puntt-neutral-gray-12"
        >
          {chat.title}
        </Text>
        {subtitle ? (
          <Text
            size="1"
            className="block truncate text-puntt-neutral-gray-11"
            data-testid={`chat-row-${chat._id}-subtitle`}
          >
            {subtitle}
          </Text>
        ) : null}
      </div>
      <Flex align="center" gap="3" className="shrink-0">
        <ChatMembersFooter members={members} chatId={chat._id} />
        <Text size="1" className="text-puntt-neutral-gray-9">
          {formatChatDate(chat.updatedAt ?? chat.createdAt)}
        </Text>
      </Flex>
    </button>
  );
}

export default function AskPunttChats() {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [view, setView] = useState<ChatsView>(getStoredView);
  const { data, isLoading, isError, isFetching } = useAskPunttAllChats();
  const currentUserId = useAppSelector((state) => state.auth.value?.userID);
  const navigate = useNavigate();

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(search), 300);
    return () => window.clearTimeout(handle);
  }, [search]);

  const chats = data?.chats ?? [];
  const total = data?.total ?? 0;

  const sortedChats = useMemo(
    () =>
      [...chats].sort(
        (a, b) =>
          new Date(b.updatedAt ?? b.createdAt ?? 0).getTime() -
          new Date(a.updatedAt ?? a.createdAt ?? 0).getTime(),
      ),
    [chats],
  );

  const filteredChats = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return sortedChats;
    return sortedChats.filter((c) => c.title.toLowerCase().includes(q));
  }, [sortedChats, debouncedSearch]);

  const showAdminBanner =
    currentUserId !== undefined &&
    chats.some((c) => c.userId !== currentUserId);

  const isSearchPending = search.trim() !== debouncedSearch.trim();
  const hasFilters = debouncedSearch.trim().length > 0;
  const listLoading = isLoading || isFetching || isSearchPending;
  const showNoResults =
    !listLoading && !isError && hasFilters && filteredChats.length === 0;
  const showEmpty =
    !listLoading && !isError && !hasFilters && filteredChats.length === 0;

  const handleViewChange = (next: ChatsView) => {
    setView(next);
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, next);
    } catch {
      // ignore
    }
  };

  const openChat = (chat: ChatListItem) => {
    navigate(askPunttChatNavLink(chat));
  };

  const handleNewChat = () => {
    navigate({ to: askPunttRoute.to });
  };

  return (
    <div
      className="flex h-full flex-col overflow-y-auto"
      data-testid="ask-puntt-chats"
    >
      <PageHeader className="justify-between gap-4">
        <Text size="5" weight="bold" className="text-puntt-accent-9">
          Chats
        </Text>
        <Flex align="center" gap="2" wrap="wrap">
          <TextField.Root
            data-testid="chats-search"
            placeholder="Search chats"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search chats"
            className="min-w-[200px]"
          >
            <TextField.Slot>
              <MagnifyingGlass size={16} aria-hidden="true" />
            </TextField.Slot>
          </TextField.Root>

          <Flex
            gap="0"
            role="group"
            aria-label="Chat view"
            className="overflow-hidden rounded-md border border-puntt-neutral-gray-6"
          >
            <button
              type="button"
              data-testid="chats-view-grid"
              aria-label="Grid view"
              aria-pressed={view === "grid"}
              className={`cursor-pointer px-2.5 py-2 transition-colors ${
                view === "grid"
                  ? "bg-puntt-accent-3 text-puntt-accent-11"
                  : "bg-base-white text-puntt-neutral-gray-9 hover:bg-puntt-neutral-gray-2"
              }`}
              onClick={() => handleViewChange("grid")}
            >
              <SquaresFour size={16} aria-hidden="true" />
            </button>
            <button
              type="button"
              data-testid="chats-view-list"
              aria-label="List view"
              aria-pressed={view === "list"}
              className={`cursor-pointer border-l border-puntt-neutral-gray-6 px-2.5 py-2 transition-colors ${
                view === "list"
                  ? "bg-puntt-accent-3 text-puntt-accent-11"
                  : "bg-base-white text-puntt-neutral-gray-9 hover:bg-puntt-neutral-gray-2"
              }`}
              onClick={() => handleViewChange("list")}
            >
              <Rows size={16} aria-hidden="true" />
            </button>
          </Flex>

          <Button
            data-testid="chats-new-button"
            onClick={handleNewChat}
            className="cursor-pointer"
          >
            <Plus size={14} weight="bold" aria-hidden="true" />
            New chat
          </Button>
        </Flex>
      </PageHeader>

      <div className="mx-auto w-full max-w-[960px] px-6 py-8">
        {showAdminBanner && (
          <Callout.Root
            color="blue"
            size="1"
            className="mb-4"
            data-testid="ask-puntt-chats-admin-banner"
          >
            <Callout.Text>
              You&apos;re viewing all chats for this enterprise as an admin.
            </Callout.Text>
          </Callout.Root>
        )}

        {total > chats.length && (
          <Text
            size="1"
            className="mb-3 block text-puntt-neutral-gray-9"
            data-testid="ask-puntt-chats-total"
          >
            Showing {chats.length} of {total} chats
          </Text>
        )}

        {listLoading ? (
          <div
            className={
              view === "grid" ? "grid grid-cols-2 gap-4" : "flex flex-col gap-2"
            }
            data-testid="chats-skeleton"
          >
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className={
                  view === "grid"
                    ? "h-40 animate-pulse rounded-2xl border border-puntt-neutral-gray-4 bg-puntt-neutral-gray-2"
                    : "h-20 animate-pulse rounded-lg border border-puntt-neutral-gray-4 bg-puntt-neutral-gray-2"
                }
              />
            ))}
          </div>
        ) : isError ? (
          <Callout.Root
            color="red"
            size="1"
            data-testid="ask-puntt-chats-error"
          >
            <Callout.Text>Failed to load chats. Please refresh.</Callout.Text>
          </Callout.Root>
        ) : showEmpty ? (
          <Flex
            direction="column"
            align="center"
            justify="center"
            gap="4"
            className="mt-16"
            data-testid="chats-empty"
          >
            <ChatCircle
              size={40}
              weight="duotone"
              className="text-puntt-neutral-gray-8"
              aria-hidden="true"
            />
            <Text
              size="4"
              weight="medium"
              className="text-puntt-neutral-gray-11"
            >
              No chats yet
            </Text>
            <Text
              size="2"
              className="max-w-sm text-center text-puntt-neutral-gray-9"
            >
              Start a conversation with Ask Puntt to see it here.
            </Text>
            <Button
              data-testid="chats-empty-new-button"
              onClick={handleNewChat}
              className="cursor-pointer"
            >
              <Plus size={14} weight="bold" aria-hidden="true" />
              New chat
            </Button>
          </Flex>
        ) : showNoResults ? (
          <Flex
            direction="column"
            align="center"
            justify="center"
            gap="3"
            className="mt-16"
            data-testid="chats-no-results"
          >
            <Text
              size="4"
              weight="medium"
              className="text-puntt-neutral-gray-11"
            >
              No chats match
            </Text>
            <Text
              size="2"
              className="max-w-sm text-center text-puntt-neutral-gray-9"
            >
              Try adjusting your search.
            </Text>
            <Button
              variant="soft"
              color="gray"
              data-testid="chats-clear-filters"
              aria-label="Clear search"
              className="cursor-pointer"
              onClick={() => setSearch("")}
            >
              Clear search
            </Button>
          </Flex>
        ) : view === "grid" ? (
          <div className="grid grid-cols-2 gap-4" data-testid="chats-grid">
            {filteredChats.map((chat) => (
              <ChatCard
                key={chat._id}
                chat={chat}
                currentUserId={currentUserId}
                onOpen={openChat}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-2" data-testid="chats-list">
            {filteredChats.map((chat) => (
              <ChatListRow
                key={chat._id}
                chat={chat}
                currentUserId={currentUserId}
                onOpen={openChat}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
